import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Users, X, CheckCircle, AlertTriangle } from 'lucide-react';

const CrewSelector = ({
  availableCrew,
  selectedCrew,
  onAddCrew,
  onRemoveCrew,
  availabilityWarnings = {},
  checkingAvailability = false,
  hasDates = true,
  testIdPrefix = ''
}) => {
  const getCrewById = (id) => availableCrew.find(c => c.id === id);
  
  const crewDailyTotal = selectedCrew.reduce((sum, crewId) => {
    const crew = getCrewById(crewId);
    return sum + (crew?.ratePerDay || 0);
  }, 0);

  const getAvailabilityIcon = (warning) => {
    if (!warning) return null;
    if (warning.is_available) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  };

  return (
    <div className="border-t pt-4">
      <Label className="flex items-center text-base font-semibold mb-3">
        <Users className="w-4 h-4 mr-2 text-purple-600" />
        Project Personeel
        {crewDailyTotal > 0 && (
          <span className="ml-auto text-sm font-normal text-green-600">
            €{crewDailyTotal.toFixed(2)}/dag
          </span>
        )}
      </Label>
      
      <div className="mb-3">
        <Select onValueChange={onAddCrew}>
          <SelectTrigger data-testid={`${testIdPrefix}crew-select-trigger`}>
            <SelectValue placeholder="Selecteer personeelsleden om toe te voegen..." />
          </SelectTrigger>
          <SelectContent>
            {availableCrew
              .filter(c => !selectedCrew.includes(c.id))
              .map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{c.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({c.role}) - €{c.ratePerDay}/dag</span>
                  </div>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCrew.length > 0 ? (
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-sm font-medium text-purple-700 mb-2">
            Geselecteerd Personeel ({selectedCrew.length} leden)
            {checkingAvailability && <span className="ml-2 text-gray-500">(beschikbaarheid controleren...)</span>}
          </p>
          <div className="space-y-2">
            {selectedCrew.map(crewId => {
              const crew = getCrewById(crewId);
              const warning = availabilityWarnings[crewId];
              return crew ? (
                <div key={crewId} className="space-y-1">
                  <div 
                    className={`flex items-center justify-between bg-white rounded-lg p-2 border ${
                      warning && !warning.is_available ? 'border-amber-300' : 'border-purple-200'
                    }`}
                  >
                    <div className="flex items-center">
                      {getAvailabilityIcon(warning)}
                      <span className="font-medium text-gray-900 ml-1">{crew.name}</span>
                      <span className="text-sm text-gray-500 ml-2">({crew.role})</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-2">€{crew.ratePerDay}/dag</span>
                      <button
                        type="button"
                        onClick={() => onRemoveCrew(crewId)}
                        className="hover:bg-purple-100 rounded-full p-0.5"
                      >
                        <X className="w-4 h-4 text-purple-600" />
                      </button>
                    </div>
                  </div>
                  {/* Beschikbaarheid Waarschuwing */}
                  {warning && !warning.is_available && (
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 ml-2">
                      <div className="flex items-start">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="text-xs">
                          <p className="font-medium text-amber-800">{warning.message}</p>
                          {warning.conflicts && warning.conflicts.length > 0 && (
                            <div className="mt-1 text-amber-700">
                              <p>Conflicterende boekingen:</p>
                              <ul className="list-disc ml-4">
                                {warning.conflicts.map((c, i) => (
                                  <li key={i}>
                                    {c.project_name} ({c.start_date} - {c.end_date})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null;
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-3 bg-gray-50 rounded-lg">
          <Users className="w-6 h-6 text-gray-300 mx-auto mb-1" />
          <p className="text-sm text-gray-500">Geen personeel toegevoegd</p>
        </div>
      )}
    </div>
  );
};

export default CrewSelector;
